import React, { useEffect, useState, useCallback, useRef } from "react";
import LoadingOverlay from "./loadingOverlay";
import "react-datepicker/dist/react-datepicker.css";
import { Box, Paper, Alert, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, MenuItem, FormControl, InputLabel, Select, Grid } from "@mui/material";
import moment from "moment";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, ComposedChart, Area, Bar, BarChart, PieChart, Pie, Cell, AreaChart
} from "recharts";

// API
import {
    solarAnalysisData,
    gridAnalysisData,
    loadAnalysisData,
    SMsolarAnalysisData,
    SMgridAnalysisData,
    SMloadAnalysisData,
} from "../api/bill";
import { co2_emmission, getGHGRows, createGHGRows, updateGHGRow, deleteGHGRow, readEmissionRate, storeEmissionRate, getGHGScope2 } from "../api/smartmeter";
import { battery_co2_emission, } from "../api/meter";
import { Tabs, Tab, OutlinedInput } from "@mui/material";

// Icon
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ForestIcon from "@mui/icons-material/Forest";
import BoltIcon from "@mui/icons-material/Bolt";

// Component
import colors from "../colour";
import { useDarkMode } from "../components/DarkModeContext";
import { ConstructionSharp } from "@mui/icons-material";

// ─── Colour tokens ────────────────────────────────────────────────────────────
const C_PURPLE = "#7e4a9d";
const C_PURPLE_LIGHT = "#b39ddb";
const C_BLUE = "#1565c0";
const C_BLUE_LIGHT = "#42a5f5";
const C_GREEN = "#2e7d32";
const C_GRID = "#f0e6ff";
const C_BORDER = "#ede7f6";

// ─── Shared chart constants ───────────────────────────────────────────────────
const CHART_MARGIN = { top: 4, right: 16, left: 0, bottom: 0 };

const commonXAxisProps = { dataKey: "date", tick: { fontSize: 12, fill: "#555" } };

const commonYAxisProps = {
    tick: { fontSize: 12, fill: "#555" },
    tickFormatter: (v) => v.toLocaleString(),
    width: 65,
};

const tooltipContentStyle = {
    backgroundColor: "#fff",
    border: `1px solid ${C_BORDER}`,
    borderRadius: 8,
    boxShadow: "0 4px 16px rgba(94,53,177,0.1)",
};

const tooltipStyle = {
    contentStyle: tooltipContentStyle,
    formatter: (value, name) => [`${Number(value).toLocaleString()} kg`, name],
};

const kwhTooltipStyle = {
    contentStyle: tooltipContentStyle,
    formatter: (value, name) => [`${Number(value).toLocaleString()} kWh`, name],
};

const legendProps = {
    verticalAlign: "top",
    align: "right",
    wrapperStyle: { paddingBottom: 10 },
    formatter: (value) => <span style={{ fontSize: 12, color: "#555" }}>{value}</span>,
};

// ─── Chart card ───────────────────────────────────────────────────────────────
const ChartCard = ({ title, children, darkMode }) => (
    <Box sx={{
        background: darkMode ? "rgba(255,255,255,0.05)" : "#fff",
        border: `1px solid ${darkMode ? "rgba(255,255,255,0.1)" : C_BORDER}`,
        borderRadius: 3,
        p: 2,
        width: "100%",
        boxShadow: darkMode ? "none" : "0 4px 16px rgba(94,53,177,0.08)",
    }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: darkMode ? "#ddd" : "#4a148c", fontSize: 20, fontWeight: "bold" }}>
            {title}
        </Typography>
        <Box sx={{ width: "100%", height: 400 }}>{children}</Box>
    </Box>
);

// ─── GHG Scope 1 — constants ─────────────────────────────────────────────────
const GHG_CATEGORIES = [
    {
        key: "stationary_combustion",
        label: "Stationary Combustion",
        info: "Emissions from burning fuels in stationary equipment (boilers, furnaces, generators).",
    },
    {
        key: "mobile_combustion",
        label: "Mobile Combustion",
        info: "Emissions from company-owned vehicles and mobile equipment.",
    },
    {
        key: "process_emissions",
        label: "Process Emissions",
        info: "Emissions from industrial processes not involving combustion (chemical reactions, etc.).",
    },
    {
        key: "fugitive_emissions",
        label: "Fugitive Emissions",
        info: "Unintentional leaks from equipment (refrigerants, natural gas leaks, etc.).",
    },
];

const GHG_UNITS = [
    "kWh", "MWh", "GJ", "liters", "m³", "kg", "tonnes",
    "gallons", "MMBtu", "therms", "miles", "km",
];

const GHG_SOURCES = [
    "Natural Gas", "Diesel", "Petrol/Gasoline", "Coal", "LPG",
    "Fuel Oil", "Biomass", "Refrigerants", "Process Gas", "Other",
];

// ─── GHG Scope 1 — sub-components ────────────────────────────────────────────
function useDebounce(fn, delay) {
    const timer = useRef(null);
    return useCallback((...args) => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => fn(...args), delay);
    }, [fn, delay]);
}

function GHGInfoTooltip({ text }) {
    const [show, setShow] = useState(false);
    return (
        <span style={{ position: "relative", display: "inline-flex" }}>
            <button
                onClick={() => setShow(s => !s)}
                onBlur={() => setShow(false)}
                style={{
                    width: 22, height: 22, borderRadius: "50%",
                    border: "1.5px solid #ccc",
                    background: "#f5f5f5",
                    color: "#666",
                    fontSize: 12, fontWeight: 500,
                    cursor: "pointer", display: "inline-flex",
                    alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                }}
                aria-label="More info"
            >i</button>
            {show && (
                <div style={{
                    position: "absolute", left: 28, top: "50%", transform: "translateY(-50%)",
                    background: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    padding: "8px 12px", fontSize: 12,
                    color: "#555",
                    width: 220, zIndex: 10, lineHeight: 1.5,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}>
                    {text}
                </div>
            )}
        </span>
    );
}

function GHGEmissionRow({ row, onUpdate, onDelete, saving }) {
    const [local, setLocal] = useState(row);

    const doPatch = useDebounce(async (id, field, value) => {
        try {
            await updateGHGRow(id, { [field]: value });
        } catch (err) {
            console.error("PATCH failed:", err);
        }
    }, 600);

    function handleChange(field, value) {
        setLocal(prev => ({ ...prev, [field]: value }));
        onUpdate(row.id, field, value);
        doPatch(row.id, field, value);
    }

    const inputSx = {
        background: "#f9f9f9",
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        px: 1.25, py: 0.75,
        fontSize: 13,
        color: "#222",
        width: "100%",
        outline: "none",
        fontFamily: "inherit",
    };

    return (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr auto", gap: 1, alignItems: "center", py: 0.75 }}>
            <select
                value={local.source_name || ""}
                onChange={e => handleChange("source_name", e.target.value)}
                style={{ ...inputSx, cursor: "pointer" }}
            >
                <option value="">— Source —</option>
                {GHG_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>

            <select
                value={local.unit_of_measurement || ""}
                onChange={e => handleChange("unit_of_measurement", e.target.value)}
                style={{ ...inputSx, cursor: "pointer" }}
            >
                <option value="">— Unit —</option>
                {GHG_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>

            <input
                type="number"
                placeholder="Usage"
                value={local.usage || ""}
                onChange={e => handleChange("usage", e.target.value)}
                style={inputSx}
            />

            <input
                type="number"
                placeholder="Emission factor"
                value={local.emission_factor || ""}
                onChange={e => handleChange("emission_factor", e.target.value)}
                style={inputSx}
            />

            <button
                onClick={() => onDelete(row.id)}
                disabled={saving}
                style={{
                    background: "none",
                    border: "1px solid #f44336",
                    borderRadius: 6,
                    color: "#f44336",
                    padding: "6px 10px",
                    fontSize: 12, cursor: "pointer",
                    opacity: saving ? 0.5 : 1,
                }}
                aria-label="Delete row"
            >
                Delete
            </button>
        </Box>
    );
}

// ─── GHG Scope 1 — main section ──────────────────────────────────────────────
function GHGScope1Section({ siteId, darkMode, selectedDate }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [addError, setAddError] = useState(null);
    const [adding, setAdding] = useState({});
    const [deleting, setDeleting] = useState({});
    const [pieData, setPieData] = useState([]);
    const [showDetails, setShowDetails] = useState(false);
    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [editingRow, setEditingRow] = useState(null);
    const [yearlyPieData, setYearlyPieData] = useState([]);
    const [yearlyLoading, setYearlyLoading] = useState(false);
    const [dialogMonth, setDialogMonth] = useState(moment(selectedDate).format("YYYY-MM"));

    const [formData, setFormData] = useState({
        source_name: "",
        unit_of_measurement: "",
        usage: "",
        emission_factor: "",
    });

    useEffect(() => {
        if (!siteId || !selectedDate || showDetails) return;
        setLoading(true);
        const monthStr = moment(selectedDate).format("YYYY-MM-01");
        getGHGRows(siteId, monthStr)
            .then(res => {
                setRows(Array.isArray(res.data) ? res.data : []);
                setLoading(false);
            })
            .catch(err => {
                setError(err.response?.data?.detail || err.message);
                setLoading(false);
            });
    }, [siteId, selectedDate]);

    useEffect(() => {
        const formattedData = Object.values(
            rows.reduce((acc, item) => {
                const category = item.category;
                const emission =
                    Number(item.calculated_emission) || 0;

                if (!category || emission <= 0) return acc;

                if (!acc[category]) {
                    acc[category] = {
                        name: category,
                        value: 0,
                    };
                }

                acc[category].value += emission;

                return acc;
            }, {})
        );

        setPieData(formattedData);
    }, [rows]);

    useEffect(() => {
        if (!showDetails) return;
        setLoading(true);
        const monthStr = moment(dialogMonth + "-01").format("YYYY-MM-01");
        getGHGRows(siteId, monthStr)
            .then(res => {
                setRows(Array.isArray(res.data) ? res.data : []);
                setLoading(false);
            })
            .catch(err => {
                setError(err.response?.data?.detail || err.message);
                setLoading(false);
            });
    }, [dialogMonth, showDetails]);

    const [yearRows, setYearRows] = useState([]);

    useEffect(() => {
        if (!siteId || !selectedDate) return;

        const fetchYearlyData = async () => {
            setYearlyLoading(true);
            const selectedYear = moment(selectedDate).year();

            try {
                const res = await getGHGRows(siteId, selectedYear);
                const allRows = Array.isArray(res.data) ? res.data : [];
                setYearRows(allRows); // ← store raw rows so the child can reuse them

                // Aggregate by category (for the donut chart)
                const aggregated = Object.values(
                    allRows.reduce((acc, item) => {
                        const category = item.category;
                        const emission = Number(item.calculated_emission) || 0;
                        if (!category || emission <= 0) return acc;
                        if (!acc[category]) acc[category] = { name: category, value: 0 };
                        acc[category].value += emission;
                        return acc;
                    }, {})
                );

                setYearlyPieData(aggregated);
            } catch (err) {
                console.error("Failed to fetch yearly GHG data:", err);
                setYearlyPieData([]);
                setYearRows([]);
            } finally {
                setYearlyLoading(false);
            }
        };

        fetchYearlyData();
    }, [siteId, selectedDate]);

    function handleAddClick(category) {
        setSelectedCategory(category);
        setEditingRow(null);
        setFormData({ source_name: "", unit_of_measurement: "", usage: "", emission_factor: "" });
        setDialogOpen(true);
    }

    function handleEditClick(row) {
        setSelectedCategory(row.category);
        setEditingRow(row);
        setFormData({
            source_name: row.source_name || "",
            unit_of_measurement: row.unit_of_measurement || "",
            usage: row.usage || "",
            emission_factor: row.emission_factor || "",
        });
        setDialogOpen(true);
    }

    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    async function handleSave() {
        setAddError(null);

        if (editingRow) {
            try {
                await updateGHGRow(editingRow.id, {
                    source_name: formData.source_name,
                    unit_of_measurement: formData.unit_of_measurement,
                    usage: formData.usage,
                    emission_factor: formData.emission_factor,
                });
                setRows(r => r.map(row =>
                    row.id === editingRow.id ? { ...row, ...formData } : row
                ));
                setDialogOpen(false);
            } catch (err) {
                console.error("UPDATE failed:", err);
                setAddError(err.response?.data?.detail || err.message);
            }
        } else {
            setAdding(a => ({ ...a, [selectedCategory]: true }));
            try {
                const payload = [{
                    client_id: siteId,
                    scope_name: "Scope 1",
                    category: selectedCategory,
                    source_name: formData.source_name,
                    unit_of_measurement: formData.unit_of_measurement,
                    usage: parseFloat(formData.usage),
                    emission_factor: parseFloat(formData.emission_factor),
                    month: moment(dialogMonth + "-01").format("YYYY-MM-01"), // ← change this line
                }];

                const res = await createGHGRows(payload);
                const created = res.data;
                const newRow = Array.isArray(created) ? created[0] : created;

                if (newRow && !newRow.id) newRow.id = `temp_${Date.now()}`;

                if (newRow) {
                    setRows(r => [...r, newRow]);
                    setDialogOpen(false);
                } else {
                    setAddError("Add succeeded but no row was returned.");
                }
            } catch (err) {
                console.error("POST failed:", err);
                setAddError(err.response?.data?.detail || err.message || "Failed to add row.");
            } finally {
                setAdding(a => ({ ...a, [selectedCategory]: false }));
            }
        }
    }

    async function handleDelete(id) {
        setDeleting(d => ({ ...d, [id]: true }));
        try {
            await deleteGHGRow(id);
            setRows(r => r.filter(row => row.id !== id));
        } catch (err) {
            console.error("DELETE failed:", err);
        } finally {
            setDeleting(d => ({ ...d, [id]: false }));
        }
    }

    function handleUpdate(id, field, value) {
        setRows(r => r.map(row => row.id === id ? { ...row, [field]: value } : row));
    }

    const BG_PANEL = "#ffffff";
    const BG_ROW = "#f6f6fb";
    const BORDER = "rgba(20, 20, 40, 0.10)";
    const TEXT_PRI = "#1f1f2e";
    const TEXT_SEC = "#6b6f85";
    const ACCENT = "#5b3fd4";
    const ACCENT_BG = "rgba(91, 63, 212, 0.08)";
    const ACCENT_BORDER = "rgba(91, 63, 212, 0.30)";

    const total = yearlyPieData.reduce((sum, d) => sum + d.value, 0);
    const pieDataWithPercent = yearlyPieData.map(d => ({
        ...d,
        percent: total ? ((d.value / total) * 100) : 0,
    }));

    // ─── Purple theme constants ───────────────────────────────────────────────────
    const DP_BG = '#ffffff';
    const DP_SURFACE = '#f5f3ff';
    const DP_BORDER = 'rgba(91,63,212,0.15)';
    const DP_TEXT_PRI = '#1e1b4b';  // deep purple, readable on white
    const DP_TEXT_SEC = '#6d5fc4';

    // Replace your old PIE_COLORS with this purple ramp
    const PIE_COLORS = ['#7F77DD', '#534AB7', '#643086', '#2e2e66', '#2a2177'];


    return (
        <>
            <Box sx={{
                background: BG_PANEL,
                border: `1px solid ${BORDER}`,
                borderRadius: 3,
                overflow: "hidden",
                width: "100%",
                boxShadow: "0 2px 12px rgba(91,63,212,0.07)",
            }}>
                {/* header */}
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2.5,
                    py: 1.75,
                    borderBottom: `1px solid ${BORDER}`,
                    background: "rgba(91,63,212,0.03)",
                }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <Typography sx={{ fontSize: 17, fontWeight: 600, color: TEXT_PRI }}>
                            Emission Breakdown
                        </Typography>
                        <Box sx={{
                            fontSize: 15,
                            fontWeight: 600,
                            background: ACCENT_BG,
                            color: ACCENT,
                            border: `1px solid ${ACCENT_BORDER}`,
                            px: 1,
                            py: 0.25,
                            borderRadius: 10,
                            letterSpacing: "0.03em",
                        }}>
                            Scope 1
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: TEXT_SEC }}>
                        {rows.length} {rows.length === 1 ? "source" : "sources"} recorded
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setShowDetails(true)}
                        sx={{
                            fontSize: 15,
                            borderRadius: 2,
                            textTransform: "none",
                            color: "#5b3fd4",
                            borderColor: "rgba(91,63,212,0.3)",
                            "&:hover": {
                                borderColor: "#5b3fd4",
                                background: "rgba(91,63,212,0.05)",
                            },
                        }}
                    >
                        Modify Details
                    </Button>
                </Box>

                {/* ── DARK PURPLE DONUT CHART SECTION ── */}
                {(yearlyLoading || yearlyPieData.length > 0) && (
                    <Box sx={{
                        p: 3,
                        borderTop: `1px solid ${BORDER}`,
                        background: DP_BG,
                        borderRadius: "0 0 12px 12px",
                    }}>

                        {/* Section label */}
                        <Typography sx={{
                            fontSize: 17,
                            fontWeight: 500,
                            color: DP_TEXT_SEC,
                            letterSpacing: "0.1em",
                            fontWeight: "bold",
                            // textTransform: "uppercase",
                            mb: 2,
                        }}>
                            Scope 1 Emission Pie Chart — {moment(selectedDate).year()}
                            {moment(selectedDate).year() === moment().year()
                                ? " (Full Year)"
                                : " (Full Year)"}
                        </Typography>
                        {yearlyLoading ? (
                            <Typography sx={{ fontSize: 13, color: DP_TEXT_SEC, textAlign: "center", py: 4 }}>
                                Loading yearly data...
                            </Typography>
                        ) : (
                            <Box sx={{ display: "flex", gap: 0, alignItems: "center", flexWrap: "wrap", }}>

                                {/* LEFT — Legend + Donut */}
                                <Box sx={{ flex: 1, minWidth: 220 }}>

                                    {/* Donut with labels outside slices */}
                                    <ResponsiveContainer width="100%" height={340}>
                                        <PieChart>
                                            <defs>
                                                {pieDataWithPercent.map((entry, index) => {
                                                    const color = PIE_COLORS[index % PIE_COLORS.length];
                                                    return (
                                                        <filter key={entry.name} id={`shadow-donut-${index}`}>
                                                            <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor={color} floodOpacity="0.4" />
                                                        </filter>
                                                    );
                                                })}
                                            </defs>
                                            <Pie
                                                data={pieDataWithPercent}
                                                cx="50%"
                                                cy="48%"
                                                outerRadius={90}
                                                innerRadius={55}
                                                paddingAngle={6}
                                                dataKey="value"
                                                nameKey="name"
                                                stroke="none"
                                                label={({ cx, cy, midAngle, outerRadius, name, percent, index }) => {
                                                    const RADIAN = Math.PI / 180;
                                                    const radius = outerRadius + 45;
                                                    const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                                    const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                                    const color = PIE_COLORS[index % PIE_COLORS.length];
                                                    return (
                                                        <text
                                                            x={x}
                                                            y={y}
                                                            fill={color}
                                                            textAnchor={x > cx ? "start" : "end"}
                                                            dominantBaseline="central"
                                                            fontSize={18}
                                                            fontWeight={600}
                                                            fontFamily="inherit"
                                                        >
                                                            {`${name} — ${percent.toFixed(1)}%`}
                                                        </text>
                                                    );
                                                }}
                                                labelLine={{ stroke: "rgba(100,100,150,0.3)", strokeWidth: 2 }}
                                            >
                                                {pieDataWithPercent.map((entry, index) => {
                                                    const color = PIE_COLORS[index % PIE_COLORS.length];
                                                    return (
                                                        <Cell
                                                            key={`cell-${index}`}
                                                            fill={color}
                                                            stroke={color}
                                                            strokeWidth={1}
                                                            style={{
                                                                filter: `drop-shadow(0px 5px 8px ${color}99)`,
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Pie>

                                            <Tooltip
                                                formatter={(value, name, props) => {
                                                    const percent = props?.payload?.percent || 0;
                                                    return [`${value.toLocaleString()} (${percent.toFixed(1)}%)`, name];
                                                }}
                                                contentStyle={{
                                                    background: DP_SURFACE,
                                                    border: "1px solid rgba(91,63,212,0.2)",
                                                    borderRadius: 10,
                                                    fontSize: 15,
                                                    color: DP_TEXT_PRI,
                                                }}
                                                labelStyle={{ color: DP_TEXT_PRI }}
                                                itemStyle={{ color: DP_TEXT_SEC }}
                                            />

                                            {/* Ground shadow ellipse via foreignObject workaround — use text as spacer */}
                                            <text x="50%" y="43%" textAnchor="middle" fill={DP_TEXT_SEC} fontSize={15} fontFamily="inherit">
                                                Total
                                            </text>
                                            <text x="50%" y="52%" textAnchor="middle" fill={DP_TEXT_PRI} fontSize={22} fontWeight={600} fontFamily="inherit">
                                                {total.toLocaleString()}
                                            </text>
                                            <text x="50%" y="60%" textAnchor="middle" fill={DP_TEXT_SEC} fontSize={13} fontFamily="inherit">
                                                tCO₂e
                                            </text>
                                        </PieChart>
                                    </ResponsiveContainer>

                                </Box>

                                {/* RIGHT — Breakdown panel 500px */}
                                <Box sx={{
                                    width: 500,
                                    minWidth: 400,
                                    background: DP_SURFACE,
                                    border: `1px solid ${DP_BORDER}`,
                                    borderRadius: 3,
                                    p: 3,
                                    alignSelf: "center",
                                }}>

                                    {/* Header */}
                                    <Box sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 2,
                                    }}>
                                        <Typography sx={{ fontSize: 17, fontWeight: 600, color: DP_TEXT_PRI }}>
                                            Breakdown
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, color: DP_TEXT_SEC }}>
                                            {pieDataWithPercent.length} sources
                                        </Typography>
                                    </Box>

                                    {/* 2x2 GRID */}
                                    <Box sx={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: 1.5,
                                        mb: 2,
                                    }}>
                                        {pieDataWithPercent.map((item, index) => {
                                            const color = PIE_COLORS[index % PIE_COLORS.length];
                                            const r = parseInt(color.slice(1, 3), 16);
                                            const g = parseInt(color.slice(3, 5), 16);
                                            const b = parseInt(color.slice(5, 7), 16);
                                            const rgb = `${r},${g},${b}`;

                                            return (
                                                <Box
                                                    key={item.name}
                                                    sx={{
                                                        p: "14px 16px",
                                                        background: `rgba(${rgb}, 0.07)`,           // ✅ tinted per item
                                                        border: `1px solid rgba(${rgb}, 0.25)`,     // ✅ tinted per item
                                                        borderRadius: 2.5,
                                                        transition: "background 0.15s",
                                                        "&:hover": {
                                                            background: `rgba(${rgb}, 0.14)`,
                                                        },
                                                    }}
                                                >
                                                    {/* Name + dot */}
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                                        <Box sx={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: "50%",
                                                            background: color,                       // ✅ matches slice
                                                            flexShrink: 0,
                                                        }} />
                                                        <Typography sx={{ fontSize: 15, color: DP_TEXT_SEC, lineHeight: 1.3 }}>
                                                            {item.name}
                                                        </Typography>
                                                    </Box>

                                                    {/* Value row */}
                                                    <Box sx={{
                                                        display: "flex",
                                                        alignItems: "baseline",
                                                        justifyContent: "space-between",
                                                    }}>
                                                        <Typography sx={{ fontSize: 22, fontWeight: 700, color: DP_TEXT_PRI, lineHeight: 1 }}>
                                                            {item.value.toLocaleString()}
                                                        </Typography>
                                                        {/* Percent badge */}
                                                        <Box sx={{
                                                            background: `rgba(${rgb}, 0.15)`,       // ✅ tinted per item
                                                            border: `1px solid rgba(${rgb}, 0.35)`, // ✅ tinted per item
                                                            borderRadius: 10,
                                                            px: 1,
                                                            py: 0.25,
                                                        }}>
                                                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: color }}> {/* ✅ matches slice */}
                                                                {item.percent.toFixed(1)}%
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    {/* Progress bar */}
                                                    <Box sx={{
                                                        mt: 1.25,
                                                        height: 4,
                                                        borderRadius: 10,
                                                        background: `rgba(${rgb}, 0.15)`,           // ✅ tinted per item
                                                        overflow: "hidden",
                                                    }}>
                                                        <Box sx={{
                                                            height: "100%",
                                                            width: `${item.percent}%`,
                                                            borderRadius: 10,
                                                            background: color,                       // ✅ matches slice
                                                        }} />
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>

                                    {/* Divider */}
                                    <Box sx={{ borderTop: `1px solid ${DP_BORDER}`, pt: 2 }}>

                                        {/* Total row */}
                                        <Box sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            p: "12px 16px",
                                            background: "rgba(127,119,221,0.07)",
                                            border: "1px solid rgba(127,119,221,0.2)",
                                            borderRadius: 2.5,
                                        }}>
                                            <Box>
                                                <Typography sx={{ fontSize: 13, color: DP_TEXT_SEC, mb: 0.25 }}>
                                                    Total Emissions
                                                </Typography>
                                                <Typography sx={{ fontSize: 13, color: DP_TEXT_SEC }}>
                                                    All sources combined
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: "right" }}>
                                                <Typography sx={{ fontSize: 26, fontWeight: 700, color: "#534AB7", lineHeight: 1 }}>
                                                    {total.toLocaleString()}
                                                </Typography>
                                                <Typography sx={{ fontSize: 13, color: DP_TEXT_SEC, mt: 0.25 }}>
                                                    tCO₂e
                                                </Typography>
                                            </Box>
                                        </Box>

                                    </Box>
                                </Box>

                            </Box>
                        )}
                    </Box>
                )}

                {/* ── YEARLY TREND CHART ── */}
                {/* ── YEARLY TREND CHART ── */}
                <GHGScope1YearlyChart siteId={siteId} selectedDate={selectedDate} yearRows={yearRows} />

                {/* ── DIALOG (unchanged) ── */}
                <Dialog
                    open={showDetails}
                    onClose={() => setShowDetails(false)}
                    maxWidth="xl"
                    fullWidth
                    PaperProps={{
                        sx: {
                            background: BG_PANEL,
                            border: `1px solid ${BORDER}`,
                            borderRadius: 3,
                        }
                    }}
                >
                    <DialogTitle sx={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: TEXT_PRI,
                        background: "rgba(91,63,212,0.03)",
                        borderBottom: `1px solid ${BORDER}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 600, color: TEXT_PRI }}>
                            Direct GHG Emissions - Modify Details
                        </Typography>

                        {/* Month Picker */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography sx={{ fontSize: 13, color: TEXT_SEC }}>Month:</Typography>
                            <OutlinedInput
                                type="month"
                                value={dialogMonth}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    const selectedYear = moment(val).year();
                                    const currentYear = moment().year();
                                    // block future months and years before 2020
                                    if (selectedYear < 2020) return;
                                    if (moment(val).isAfter(moment(), "month")) return;
                                    setDialogMonth(val);
                                }}
                                inputProps={{
                                    min: "2020-01",
                                    max: moment().format("YYYY-MM"),
                                }}
                                sx={{
                                    height: "32px",
                                    width: "160px",
                                    fontSize: 13,
                                    color: TEXT_PRI,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: "rgba(91,63,212,0.3)",
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: "#5b3fd4",
                                    },
                                    input: { color: TEXT_PRI, fontSize: 13 }
                                }}
                            />
                        </Box>
                    </DialogTitle>

                    <DialogContent sx={{ p: 0 }}>
                        <Box sx={{ px: 2.5, py: 1 }}>

                            {loading && (
                                <Typography sx={{ fontSize: 13, color: TEXT_SEC, textAlign: "center", py: 3 }}>
                                    Loading emissions data…
                                </Typography>
                            )}

                            {error && (
                                <Alert severity="error" sx={{ my: 2 }}>
                                    Failed to load GHG data: {error}
                                </Alert>
                            )}

                            {addError && (
                                <Alert severity="warning" sx={{ my: 2 }} onClose={() => setAddError(null)}>
                                    {addError}
                                </Alert>
                            )}

                            {!loading && GHG_CATEGORIES.map((cat, ci) => {
                                const catRows = rows.filter(r => r.category === cat.label);
                                return (
                                    <Box
                                        key={cat.key}
                                        sx={{
                                            py: 2,
                                            borderBottom: ci < GHG_CATEGORIES.length - 1
                                                ? `1px solid ${BORDER}`
                                                : "none",
                                        }}
                                    >
                                        {/* category header row */}
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: catRows.length ? 1.5 : 1 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Box sx={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: ACCENT,
                                                    background: ACCENT_BG,
                                                    border: `1px solid ${ACCENT_BORDER}`,
                                                    px: 1.25,
                                                    py: 0.5,
                                                    borderRadius: 10,
                                                }}>
                                                    {cat.label}
                                                </Box>
                                                <GHGInfoTooltip text={cat.info} />
                                            </Box>

                                            <button
                                                onClick={() => handleAddClick(cat.label)}
                                                disabled={adding[cat.key]}
                                                style={{
                                                    display: "flex", alignItems: "center", gap: 6,
                                                    background: "rgba(91,63,212,0.08)",
                                                    border: "1px solid rgba(91,63,212,0.30)",
                                                    borderRadius: 8,
                                                    color: "#5b3fd4",
                                                    padding: "6px 14px",
                                                    fontSize: 13,
                                                    cursor: "pointer",
                                                    opacity: adding[cat.key] ? 0.5 : 1,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                + Add
                                            </button>
                                        </Box>

                                        {/* empty state */}
                                        {catRows.length === 0 && (
                                            <Box sx={{
                                                py: 1.5,
                                                px: 2,
                                                fontSize: 13,
                                                color: TEXT_SEC,
                                                background: ACCENT_BG,
                                                borderRadius: 1.5,
                                                border: `1px dashed ${ACCENT_BORDER}`,
                                                textAlign: "center",
                                            }}>
                                                No entries yet — click Add to record a source
                                            </Box>
                                        )}

                                        {/* table rows */}
                                        {catRows.length > 0 && (
                                            <>
                                                <Box sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: "minmax(0,2fr) minmax(0,1.2fr) minmax(0,0.8fr) minmax(0,0.8fr) 72px",
                                                    gap: 1,
                                                    px: 1,
                                                    pb: 0.75,
                                                }}>
                                                    {["Source", "Unit", "Usage", "Factor", "Actions"].map((h, i) => (
                                                        <Typography key={i} sx={{
                                                            fontSize: 11,
                                                            color: TEXT_SEC,
                                                            fontWeight: 600,
                                                            textTransform: "uppercase",
                                                            letterSpacing: "0.05em",
                                                        }}>
                                                            {h}
                                                        </Typography>
                                                    ))}
                                                </Box>

                                                {catRows.map(row => (
                                                    <Box key={row.id} sx={{
                                                        display: "grid",
                                                        gridTemplateColumns: "minmax(0,2fr) minmax(0,1.2fr) minmax(0,0.8fr) minmax(0,0.8fr) 72px",
                                                        gap: 1,
                                                        alignItems: "center",
                                                        background: BG_ROW,
                                                        border: `1px solid ${BORDER}`,
                                                        borderRadius: 1.5,
                                                        px: 1,
                                                        py: 0.75,
                                                        mb: 0.75,
                                                    }}>
                                                        <Box>{row.source_name}</Box>
                                                        <Box>{row.unit_of_measurement}</Box>
                                                        <Box>{row.usage}</Box>
                                                        <Box>{row.emission_factor}</Box>
                                                        <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                                                            <Box component="button" onClick={() => handleEditClick(row)}>✏</Box>
                                                            <Box component="button" onClick={() => handleDelete(row.id)}>🗑</Box>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ background: "rgba(91,63,212,0.03)" }}>
                        <Button onClick={() => setShowDetails(false)}>Close</Button>
                    </DialogActions>
                </Dialog>

                {/* footer */}
                {!loading && (
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 2.5,
                        py: 1.25,
                        borderTop: `1px solid ${BORDER}`,
                        background: "rgba(91,63,212,0.03)",
                    }}>
                        <Typography sx={{ fontSize: 12, color: TEXT_SEC }}>
                            {rows.length} emission {rows.length === 1 ? "source" : "sources"} recorded
                        </Typography>
                    </Box>
                )}

            </Box>

            {/* Add/Edit Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        background: "#ffffff",
                        border: `1px solid ${BORDER}`,
                        borderRadius: 3,
                        boxShadow: "0 20px 60px rgba(91,63,212,0.15)",
                    }
                }}
            >
                <DialogTitle sx={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: TEXT_PRI,
                    pb: 1,
                    borderBottom: `1px solid ${BORDER}`,
                    background: "rgba(91,63,212,0.03)",
                }}>
                    {editingRow ? "Edit emission source" : `Add ${selectedCategory?.toLowerCase()} source`}
                </DialogTitle>

                <DialogContent sx={{ pt: "16px !important" }}>
                    {addError && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAddError(null)}>
                            {addError}
                        </Alert>
                    )}
                    <Grid container spacing={1.5}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ color: TEXT_SEC }}>Source name</InputLabel>
                                <Select
                                    value={formData.source_name}
                                    onChange={(e) => handleFormChange("source_name", e.target.value)}
                                    label="Source name"
                                    sx={{ color: TEXT_PRI }}
                                >
                                    {GHG_SOURCES.map(s => (
                                        <MenuItem key={s} value={s}>{s}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ color: TEXT_SEC }}>Unit of measurement</InputLabel>
                                <Select
                                    value={formData.unit_of_measurement}
                                    onChange={(e) => handleFormChange("unit_of_measurement", e.target.value)}
                                    label="Unit of measurement"
                                    sx={{ color: TEXT_PRI }}
                                >
                                    {GHG_UNITS.map(u => (
                                        <MenuItem key={u} value={u}>{u}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                fullWidth size="small" type="number" label="Usage"
                                value={formData.usage}
                                onChange={(e) => handleFormChange("usage", e.target.value)}
                                placeholder="e.g. 4200"
                                InputLabelProps={{ sx: { color: TEXT_SEC } }}
                                inputProps={{ sx: { color: TEXT_PRI } }}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                fullWidth size="small" type="number" label="Emission factor"
                                value={formData.emission_factor}
                                onChange={(e) => handleFormChange("emission_factor", e.target.value)}
                                placeholder="e.g. 1.9"
                                helperText="CO₂e per unit"
                                InputLabelProps={{ sx: { color: TEXT_SEC } }}
                                inputProps={{ sx: { color: TEXT_PRI } }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{
                    px: 2, pb: 2, pt: 1, gap: 1,
                    borderTop: `1px solid ${BORDER}`,
                    background: "rgba(91,63,212,0.03)",
                }}>
                    <Button
                        onClick={() => setDialogOpen(false)}
                        size="small"
                        sx={{ fontSize: 13, color: TEXT_SEC, borderRadius: 2 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        size="small"
                        disableElevation
                        sx={{
                            fontSize: 13, fontWeight: 600,
                            borderRadius: 2,
                            bgcolor: ACCENT,
                            "&:hover": { bgcolor: "#4a32b8" },
                        }}
                    >
                        {editingRow ? "Save changes" : "Add source"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

// ─── Add this new component above GHGScope1Section ───────────────────────────
function GHGScope1YearlyChart({ siteId, selectedDate, yearRows }) {
    const [monthlyData, setMonthlyData] = useState([]);

    const SCOPE1_CATEGORIES = [
        "Mobile Combustion",
        "Process Emissions",
        "Stationary Combustion",
        "Fugitive Emissions",
    ];

    const PIE_COLORS = {
        "Mobile Combustion": "#7F77DD",
        "Process Emissions": "#534AB7",
        "Stationary Combustion": "#9B59B6",
        "Fugitive Emissions": "#2e2e66",
    };

    useEffect(() => {
        if (!selectedDate || !Array.isArray(yearRows)) return;

        const selectedYear = moment(selectedDate).year();
        const currentYear = moment().year();
        const totalMonths = selectedYear === currentYear ? moment().month() + 1 : 12;

        const rowsByMonth = yearRows.reduce((acc, item) => {
            const monthKey = moment(item.month).format("YYYY-MM"); // ⚠️ adjust "item.month" if the field name differs
            if (!acc[monthKey]) acc[monthKey] = [];
            acc[monthKey].push(item);
            return acc;
        }, {});

        const formatted = Array.from({ length: totalMonths }, (_, i) => {
            const monthKey = moment({ year: selectedYear, month: i }).format("YYYY-MM");
            const rows = rowsByMonth[monthKey] || [];

            const pieData = SCOPE1_CATEGORIES
                .map(cat => ({
                    name: cat,
                    value: parseFloat(
                        rows
                            .filter(r => r.category === cat)
                            .reduce((sum, r) => sum + (Number(r.calculated_emission) || 0), 0)
                            .toFixed(2)
                    )
                }))
                .filter(d => d.value > 0);

            return {
                month: moment({ year: selectedYear, month: i }).format("MMMM"),
                pieData,
                total: pieData.reduce((sum, d) => sum + d.value, 0),
            };
        });

        setMonthlyData(formatted);
    }, [selectedDate, yearRows]);

    if (!yearRows) return (
        <Box sx={{ px: 3, pb: 3, pt: 2, fontSize: 13, color: "#6d5fc4" }}>
            Loading monthly trend…
        </Box>
    );

    if (!monthlyData.length) return null;

    return (
        <Box sx={{ px: 3, pb: 3, pt: 2.5, borderTop: "1px solid rgba(20,20,40,0.10)" }}>

            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
                <Typography sx={{ fontSize: 17, fontWeight: "bold", color: "#6d5fc4" }}>
                    Scope 1 Monthly Breakdown — {moment(selectedDate).year()}
                </Typography>

            </Box>

            {/* Legend */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
                {SCOPE1_CATEGORIES.map(cat => (
                    <Box key={cat} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: 0.5, background: PIE_COLORS[cat] }} />
                        <Typography sx={{ fontSize: 12, color: "#6d5fc4" }}>{cat}</Typography>
                    </Box>
                ))}
            </Box>

            {/* Grid of pie charts */}
            <Box sx={{
                display: "grid",
                gridTemplateColumns: "repeat(6, 1fr)",
                gap: 3,
                // background: "linear-gradient(135deg, #ede9fe 0%, #ddd6fe 50%, #c4b5fd 100%)",
                borderRadius: 3,
                p: 3,
            }}>
                {monthlyData.map(({ month, pieData, total }) => (
                    <Box key={month} sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        background: "rgba(255, 255, 255, 0.25)",
                        backdropFilter: "blur(12px)",
                        WebkitBackdropFilter: "blur(12px)",
                        border: "1px solid rgba(255, 255, 255, 0.4)",
                        borderRadius: 4,
                        p: 2.5,
                        boxShadow: "0 4px 24px rgba(91,63,212,0.12), inset 0 1px 0 rgba(255,255,255,0.6)",
                        transition: "all 0.2s ease",
                        "&:hover": {
                            boxShadow: "0 8px 32px rgba(91,63,212,0.2), inset 0 1px 0 rgba(255,255,255,0.6)",
                            transform: "translateY(-3px)",
                            background: "rgba(255, 255, 255, 0.35)",
                            border: "1px solid rgba(255,255,255,0.6)",
                        }
                    }}>
                        {/* Month label with pill */}
                        <Box sx={{
                            background: "rgba(91,63,212,0.1)",
                            border: "1px solid rgba(91,63,212,0.25)",
                            borderRadius: 10,
                            px: 1.5,
                            py: 0.4,
                            mb: 1.5,
                        }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#5b3fd4", letterSpacing: "0.05em" }}>
                                {month}
                            </Typography>
                        </Box>

                        {pieData.length > 0 ? (
                            <>
                                {/* Pie chart with total in center */}
                                <Box sx={{ position: "relative", width: 200, height: 185 }}>
                                    {/* 3D shadow layer */}
                                    <Box sx={{
                                        position: "absolute",
                                        bottom: 0,
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        width: 160,
                                        height: 20,
                                        borderRadius: "50%",
                                        background: "rgba(91,63,212,0.25)",
                                        filter: "blur(8px)",
                                        zIndex: 0,
                                    }} />

                                    <Box sx={{ position: "relative", zIndex: 1 }}>
                                        <PieChart width={200} height={200}>
                                            <defs>
                                                {pieData.map((entry) => (
                                                    <filter key={entry.name} id={`shadow-${month}-${entry.name.replace(/\s/g, "")}`}>
                                                        <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor={PIE_COLORS[entry.name]} floodOpacity="0.4" />
                                                    </filter>
                                                ))}
                                            </defs>
                                            <Pie
                                                data={pieData}
                                                cx="50%"
                                                cy="45%"
                                                outerRadius={85}
                                                innerRadius={50}
                                                paddingAngle={3}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                {pieData.map((entry, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={PIE_COLORS[entry.name]}
                                                        style={{
                                                            filter: `drop-shadow(0px 4px 6px ${PIE_COLORS[entry.name]}88)`,
                                                        }}
                                                        stroke={PIE_COLORS[entry.name]}
                                                        strokeWidth={1}
                                                    />
                                                ))}
                                            </Pie>

                                            {/* Center total text */}
                                            <text x="50%" y="38%" textAnchor="middle" fill="rgba(91,63,212,0.6)" fontSize={11} fontFamily="inherit">
                                                TOTAL
                                            </text>
                                            <text x="50%" y="48%" textAnchor="middle" fill="#1e1b4b" fontSize={17} fontWeight={700} fontFamily="inherit">
                                                {total.toLocaleString()}
                                            </text>
                                            <text x="50%" y="57%" textAnchor="middle" fill="rgba(91,63,212,0.6)" fontSize={10} fontFamily="inherit">
                                                tCO₂e
                                            </text>

                                            <Tooltip
                                                formatter={(value, name) => [
                                                    `${value.toLocaleString()} tCO₂e`,
                                                    name
                                                ]}
                                                contentStyle={{
                                                    background: "#f5f3ff",
                                                    border: "1px solid rgba(91,63,212,0.2)",
                                                    borderRadius: 8,
                                                    fontSize: 12,
                                                    color: "#1e1b4b",
                                                }}
                                            />
                                        </PieChart>
                                    </Box>
                                </Box>

                                {/* Divider */}
                                <Box sx={{ width: "90%", height: "1px", background: "rgba(91,63,212,0.15)", my: 1.5 }} />

                                {/* Emission breakdown below chart */}
                                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
                                    {SCOPE1_CATEGORIES.map(cat => {
                                        const entry = pieData.find(d => d.name === cat);
                                        const value = entry?.value || 0;
                                        const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                        return (
                                            <Box key={cat} sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                px: 0.5,
                                                opacity: value > 0 ? 1 : 0.35,
                                            }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1 }}>
                                                    <Box sx={{
                                                        width: 8, height: 8,
                                                        borderRadius: "50%",
                                                        background: PIE_COLORS[cat],
                                                        flexShrink: 0,
                                                    }} />
                                                    <Typography sx={{ fontSize: 12, color: "#6d5fc4", lineHeight: 1.2 }}>
                                                        {cat.replace(" Combustion", "").replace(" Emissions", "")}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#1e1b4b" }}>
                                                        {value > 0 ? value.toLocaleString() : "—"}
                                                    </Typography>
                                                    {value > 0 && (
                                                        <Typography sx={{ fontSize: 11, color: "rgba(91,63,212,0.6)" }}>
                                                            ({percent}%)
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </>
                        ) : (
                            <Box sx={{
                                width: 200,
                                height: 200,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                            }}>
                                <Box sx={{
                                    width: 60, height: 60, borderRadius: "50%",
                                    background: "rgba(91,63,212,0.08)",
                                    border: "2px dashed rgba(91,63,212,0.2)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                    <Typography sx={{ fontSize: 24, color: "rgba(91,63,212,0.3)" }}>—</Typography>
                                </Box>
                                <Typography sx={{ fontSize: 13, color: "rgba(109,95,196,0.5)", textAlign: "center" }}>
                                    No data
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}



// ─── Main Component ───────────────────────────────────────────────────────────
const AnalysisSection = ({ selectedDate, siteId, solarViewType, userType, onTabChange }) => {
    const [solarAnalysis, setSolarAnalysis] = useState([]);
    const [gridAnalysis, setGridAnalysis] = useState([]);
    const [loadAnalysis, setLoadAnalysis] = useState([]);
    const [co2Data, setCo2Data] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { darkMode } = useDarkMode();
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(1);
    const [emissionRate, setEmissionRate] = useState("");
    const [erDialogOpen, setErDialogOpen] = useState(false);
    const [erSaved, setErSaved] = useState(false);
    const [batteryCo2Data, setBatteryCo2Data] = useState([]);


    useEffect(() => {
        const year = moment(selectedDate).format("YYYY");
        readEmissionRate(siteId, year)
            .then(res => {
                setEmissionRate(res.data?.emission_rate_kgco2_per_kwh ?? "");
            })
            .catch(err => console.error("Failed to read ER:", err));
    }, [siteId, selectedDate]);

    const handleSaveEmissionRate = async () => {
        try {
            const year = moment(selectedDate).format("YYYY");
            await storeEmissionRate(siteId, year, emissionRate);
        } catch (err) {
            console.error("Failed to store ER:", err);
        }
    };

    useEffect(() => {
        getGHGScope2(siteId, moment(selectedDate).format("YYYY-MM-01"))
            .then(res => {
            })
            .catch(err => {
                console.error("Failed to fetch GHG Scope 2:", err);
            });
    }, [siteId]);

    useEffect(() => {
        if (userType === null) return;
        setIsLoading(true);

        const formattedDate =
            solarViewType === "day"
                ? moment(selectedDate).format("YYYY-MM")
                : moment(selectedDate).format("YYYY");

        const fetchData = async () => {
            setError(null);
            const results = await Promise.allSettled([
                solarAnalysisData(siteId, moment(selectedDate).format("YYYY-MM")),
                gridAnalysisData(siteId, moment(selectedDate).format("YYYY-MM")),
                loadAnalysisData(siteId, moment(selectedDate).format("YYYY-MM")),
                battery_co2_emission(siteId, formattedDate),

            ]);
            const [solarRes, gridRes, loadRes, co2Res,] = results;
            let hasAnySuccess = false;
            if (solarRes.status === "fulfilled") { setSolarAnalysis(solarRes.value.data); hasAnySuccess = true; }
            if (gridRes.status === "fulfilled") { setGridAnalysis(gridRes.value.data); hasAnySuccess = true; }
            if (loadRes.status === "fulfilled") { setLoadAnalysis(loadRes.value.data); hasAnySuccess = true; }
            if (co2Res.status === "fulfilled") {
                setCo2Data(co2Res.value?.data?.graph_data ?? []);
                hasAnySuccess = true;
            }

            if (!hasAnySuccess) setError("No data available for this period");
            setIsLoading(false);
        };

        const SMfetchData = async () => {
            try {
                const [solarData, gridData, loadData, co2Res] = await Promise.all([
                    SMsolarAnalysisData(siteId, formattedDate),
                    SMgridAnalysisData(siteId, formattedDate),
                    SMloadAnalysisData(siteId, formattedDate),
                    co2_emmission(siteId, formattedDate),
                ]);
                setSolarAnalysis((prev) => prev !== solarData.data ? solarData.data : prev);
                setGridAnalysis((prev) => prev !== gridData.data ? gridData.data : prev);
                setLoadAnalysis((prev) => prev !== loadData.data ? loadData.data : prev);
                setCo2Data(co2Res?.data?.graph_data ?? []);
            } catch (err) {
                console.error("Error fetching data", err);
                setError("Failed to fetch data");
            } finally {
                setIsLoading(false);
            }
        };

        if (userType === 1) SMfetchData();
        else fetchData();
    }, [selectedDate, siteId, solarViewType, userType]);

    // ── Transform API data into Recharts format ───────────────────────────────
    const chartData = co2Data.map((d) => ({
        date: solarViewType === "day"
            ? moment(d.recorded_at).format("MMM D")      // e.g. "Jun 3"
            : moment(d.recorded_at).format("MMM YYYY"),  // e.g. "Jun 2025"
        gridImportCO2: +parseFloat(d.grid_import_co2_kg).toFixed(1),
        gridExportCO2: +parseFloat(d.grid_export_co2_kg).toFixed(1),
        buildingLoadCO2: +parseFloat(d.building_load_co2_kg).toFixed(1),
        solarAvoided: +parseFloat(d.solar_co2_avoided_kg).toFixed(1),
        gridImportKwh: +parseFloat(d.grid_import_kwh).toFixed(1),
        solarKwh: +parseFloat(d.solar_kwh).toFixed(1),
        buildingLoadKwh: +parseFloat(d.building_load_kwh).toFixed(1),
    }));

    if (isLoading) return <LoadingOverlay />;

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="20vh">
                <Paper elevation={1} style={{ width: "40vh" }}>
                    <Alert severity="error">{error}</Alert>
                </Paper>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ backgroundColor: darkMode ? "rgba(255,255,255,0.1)" : colors.l7, borderRadius: "20px" }}>

                {/* ── TABS ── */}
                <Box sx={{ borderBottom: 1, borderColor: darkMode ? colors.f1 : colors.g5, mb: "2vh" }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, val) => {
                            setActiveTab(val);
                            onTabChange?.(val);
                        }}
                        centered
                        sx={{
                            "& .MuiTabs-indicator": {
                                backgroundColor: darkMode ? colors.d4 : colors.l4,
                            },
                            "& .Mui-selected": {
                                color: darkMode ? colors.d4 : colors.l4,
                            },
                        }}
                    >
                        <Tab
                            label="Scope 1"
                            sx={{
                                color: darkMode ? colors.f2 : colors.f3,
                                fontSize: "1vw",
                                "&.Mui-selected": { color: darkMode ? colors.d4 : colors.l4 },
                            }}
                        />
                        <Tab
                            label="Scope 2"
                            sx={{
                                color: darkMode ? colors.f2 : colors.f3,
                                fontSize: "1vw",
                                "&.Mui-selected": { color: darkMode ? colors.d4 : colors.l4 },
                            }}
                        />
                    </Tabs>
                </Box>

                {/* ── TAB 1: Analysis ── */}
                {activeTab === 1 && (
                    <>

                        {/* ── Emission Rate ── */}
                        {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, pb: 1, justifyContent: "flex-end" }}>
                            <Button
                                variant="outlined"
                                onClick={() => setErDialogOpen(true)}
                                sx={{
                                    fontSize: 15,
                                    borderRadius: 2,
                                    textTransform: "none",
                                    color: "#5b3fd4",
                                    borderColor: "rgba(91,63,212,0.3)",
                                    "&:hover": {
                                        borderColor: "#5b3fd4",
                                        background: "rgba(91,63,212,0.05)",
                                    },
                                }}
                            >
                                Modify Emission Rate
                            </Button>
                        </Box>
                        <Dialog
                            open={erDialogOpen}
                            onClose={() => setErDialogOpen(false)}
                            maxWidth="xs"
                            fullWidth
                            PaperProps={{
                                sx: {
                                    borderRadius: 3,
                                    border: "1px solid rgba(91,63,212,0.15)",
                                }
                            }}
                        >
                            <DialogTitle sx={{
                                fontSize: 15,
                                fontWeight: 600,
                                color: "#1e1b4b",
                                borderBottom: "1px solid rgba(91,63,212,0.1)",
                                background: "rgba(91,63,212,0.03)",
                            }}>
                                Set Emission Rate
                            </DialogTitle>

                            <DialogContent sx={{ pt: "16px !important" }}>
                                <Typography sx={{ fontSize: 13, color: "#6d5fc4", mb: 2 }}>
                                    Set the emission rate for {moment(selectedDate).format("YYYY")}.
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e1b4b" }}>
                                        Emission Rate (tCO₂e):
                                    </Typography>
                                    <input
                                        type="number"
                                        value={emissionRate}
                                        onChange={e => {
                                            setEmissionRate(e.target.value);
                                            setErSaved(false);
                                        }}
                                        placeholder="e.g. 0.771"
                                        style={{
                                            width: 120,
                                            padding: "6px 10px",
                                            border: `1px solid ${erSaved ? "#2e7d32" : "rgba(91,63,212,0.3)"}`,
                                            borderRadius: 6,
                                            fontSize: 13,
                                            color: "#1e1b4b",
                                            outline: "none",
                                            fontFamily: "inherit",
                                        }}
                                    />
                                    {erSaved && (
                                        <Typography sx={{ color: "#2e7d32", fontSize: 20, lineHeight: 1 }}>✓</Typography>
                                    )}
                                </Box>
                            </DialogContent>

                            <DialogActions sx={{
                                background: "rgba(91,63,212,0.03)",
                                borderTop: "1px solid rgba(91,63,212,0.1)",
                                px: 2, pb: 2,
                            }}>
                                <Button
                                    onClick={() => setErDialogOpen(false)}
                                    sx={{ fontSize: 13, color: "#6d5fc4", borderRadius: 2 }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={async () => {
                                        await handleSaveEmissionRate();
                                        setErDialogOpen(false);
                                    }}
                                    variant="contained"
                                    disableElevation
                                    sx={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        bgcolor: "#5b3fd4",
                                        "&:hover": { bgcolor: "#4a32b8" },
                                    }}
                                >
                                    Save Rate
                                </Button>
                            </DialogActions>
                        </Dialog> */}

                        {/* ── Summary cards ─────────────────────────────────────────── */}
                        <Box>
                            <Box sx={{
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                width: "100%", height: { xs: "50vh", md: "30vh" },
                                padding: 2, flexDirection: { xs: "column", md: "row" },
                            }}>
                                <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between", marginBottom: { xs: "10px", md: "0" } }}>

                                    {/* CO2 Emissions */}
                                    <Box sx={{ background: darkMode ? colors.d1 : colors.l4, display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "flex-start", width: "32%", height: "100%", borderRadius: 3 }}>
                                        <Box sx={{ width: "40%", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "1vh" }}>
                                            <DirectionsCarIcon sx={{ fontSize: "100%", color: "#ebebeb", width: "40%", height: "40%" }} />
                                            <Typography sx={{ fontSize: { xs: "0.9vh", md: "2vh" }, textAlign: "center", color: "#ebebeb", marginTop: "1vh", padding: "1vh", fontWeight: "bold", width: "80%" }}>
                                                CO<sub>2</sub> Emissions In Tonnes
                                            </Typography>
                                        </Box>
                                        <Box sx={{ width: "60%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "5%" }}>
                                            <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "18%", backgroundColor: colors.b1, borderRadius: "15px", overflow: "hidden" }}>
                                                <Typography sx={{ fontSize: { xs: "1vh", md: "2.2vh" }, textAlign: "center", fontWeight: "bold", color: colors.f2 }}>
                                                    Solar: -{(parseFloat(solarAnalysis["co2_emissions_avoided_tonnes"]) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Tonnes
                                                </Typography>
                                                <Typography sx={{ fontSize: { xs: "1vh", md: "2.2vh" }, textAlign: "center", fontWeight: "bold", color: colors.f2 }}>
                                                    Grid: {(parseFloat(gridAnalysis["co2_emissions_avoided_tonnes"]) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Tonnes
                                                </Typography>
                                                <Typography sx={{ fontSize: { xs: "1vh", md: "2.2vh" }, textAlign: "center", fontWeight: "bold", color: colors.f2 }}>
                                                    Load: {(parseFloat(loadAnalysis["co2_emissions_avoided_tonnes"]) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Tonnes
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Equivalent Trees */}
                                    <Box sx={{ background: darkMode ? colors.d1 : colors.l4, display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "flex-start", width: "32%", height: "100%", borderRadius: 3 }}>
                                        <Box sx={{ width: "40%", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "1vh" }}>
                                            <ForestIcon sx={{ fontSize: "100%", color: "#ebebeb", width: "40%", height: "40%" }} />
                                            <Typography sx={{ fontSize: { xs: "0.9vh", md: "2vh" }, textAlign: "center", color: "#ebebeb", marginTop: "1vh", padding: "1vh", fontWeight: "bold", width: "80%" }}>
                                                Equivalent Trees
                                            </Typography>
                                        </Box>
                                        <Box sx={{ width: "60%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "5%" }}>
                                            <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "18%", backgroundColor: colors.b1, borderRadius: "15px", overflow: "hidden" }}>
                                                <Typography sx={{ fontSize: { xs: "1vh", md: "2.2vh" }, textAlign: "center", fontWeight: "bold", color: colors.f2 }}>
                                                    Solar: {(parseFloat(solarAnalysis["equivalent_trees_planted"]) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Trees
                                                </Typography>
                                                <Typography sx={{ fontSize: { xs: "1vh", md: "2.2vh" }, textAlign: "center", fontWeight: "bold", color: colors.f2 }}>
                                                    Grid: -{(parseFloat(gridAnalysis["equivalent_trees_planted"]) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Trees
                                                </Typography>
                                                <Typography sx={{ fontSize: { xs: "1vh", md: "2.2vh" }, textAlign: "center", fontWeight: "bold", color: colors.f2 }}>
                                                    Load: -{(parseFloat(loadAnalysis["equivalent_trees_planted"]) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Trees
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Consumption Used */}
                                    <Box sx={{ background: darkMode ? colors.d1 : colors.l4, display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "flex-start", width: "32%", height: "100%", borderRadius: 3 }}>
                                        <Box sx={{ width: "40%", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "1vh" }}>
                                            <BoltIcon sx={{ fontSize: "100%", color: "#ebebeb", width: "40%", height: "40%" }} />
                                            <Typography sx={{ fontSize: { xs: "0.9vh", md: "2vh" }, textAlign: "center", color: "#ebebeb", marginTop: "1vh", padding: "1vh", fontWeight: "bold", width: "80%" }}>
                                                Consumption Used
                                            </Typography>
                                        </Box>
                                        <Box sx={{ width: "60%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "5%" }}>
                                            <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "18%", backgroundColor: colors.b1, borderRadius: "15px", overflow: "hidden" }}>
                                                <Typography sx={{ fontSize: { xs: "1vh", md: "2.2vh" }, textAlign: "center", fontWeight: "bold", color: colors.f2 }}>
                                                    Solar: {solarAnalysis["total_solar_generated_kWh"] > 1000
                                                        ? `${(parseFloat(solarAnalysis["total_solar_generated_kWh"]) / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MWh`
                                                        : `${(parseFloat(solarAnalysis["total_solar_generated_kWh"]) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KWh`}
                                                </Typography>
                                                <Typography sx={{ fontSize: { xs: "1vh", md: "2.2vh" }, textAlign: "center", fontWeight: "bold", color: colors.f2 }}>
                                                    Grid: {gridAnalysis["total_grid_consumption_kWh"] > 1000
                                                        ? `${(parseFloat(gridAnalysis["total_grid_consumption_kWh"]) / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MWh`
                                                        : `${(parseFloat(gridAnalysis["total_grid_consumption_kWh"]) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KWh`}
                                                </Typography>
                                                <Typography sx={{ fontSize: { xs: "1vh", md: "2.2vh" }, textAlign: "center", fontWeight: "bold", color: colors.f2 }}>
                                                    Load: {loadAnalysis["total_load_consumption_kWh"] > 1000
                                                        ? `${(parseFloat(loadAnalysis["total_load_consumption_kWh"]) / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MWh`
                                                        : `${(parseFloat(loadAnalysis["total_load_consumption_kWh"]) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KWh`}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                </Box>
                            </Box>
                        </Box>

                        {/* ── Charts + Scope 2 tracker ──────────────────────────────── */}
                        {chartData.length > 0 && (
                            <Box sx={{ p: 2, pt: 1 }}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>

                                    {/* Chart — Daily CO₂ breakdown */}
                                    <ChartCard
                                        title={solarViewType === "day" ? "Daily CO₂ Breakdown (kg)" : "Monthly CO₂ Breakdown (kg)"}
                                        darkMode={darkMode}
                                    >
                                        <ResponsiveContainer width="100%" height={400}>
                                            <ComposedChart data={chartData} margin={CHART_MARGIN}>
                                                <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "rgba(255,255,255,0.07)" : C_GRID} />
                                                <XAxis {...commonXAxisProps} />
                                                <YAxis {...commonYAxisProps} />
                                                <Tooltip
                                                    contentStyle={tooltipContentStyle}
                                                    formatter={(value, name) => [`${Number(value).toLocaleString()} kg`, name]}
                                                    labelFormatter={(label) => <b>{label}</b>}
                                                />
                                                <Legend
                                                    verticalAlign="bottom"
                                                    align="center"
                                                    wrapperStyle={{ paddingTop: 20 }}
                                                    formatter={(value) => {
                                                        let color = "#555";
                                                        if (value === "Grid export CO₂") color = "#b39ddb";
                                                        if (value === "Grid import CO₂") color = "#7e4a9d";
                                                        if (value === "Solar CO₂ avoided") color = "#90caf9";
                                                        if (value === "Building load CO₂ (total)") color = "#1565c0";
                                                        return <span style={{ fontSize: 15, color: color }}>{value}</span>;
                                                    }}
                                                    iconType="square"
                                                />
                                                <Bar name="Grid export CO₂" dataKey="gridExportCO2" fill={C_PURPLE} fillOpacity={0.5} radius={[4, 4, 0, 0]} barSize={20} />
                                                <Bar name="Grid import CO₂" dataKey="gridImportCO2" fill={C_PURPLE} radius={[4, 4, 0, 0]} barSize={20} />
                                                <Bar name="Solar CO₂ avoided" dataKey="solarAvoided" fill={C_BLUE_LIGHT} radius={[4, 4, 0, 0]} barSize={20} />
                                                <Line
                                                    name="Building load CO₂ (total)"
                                                    type="monotone"
                                                    dataKey="buildingLoadCO2"
                                                    stroke={C_BLUE}
                                                    strokeWidth={2}
                                                    strokeDasharray="5 3"
                                                    dot={{ r: 4, fill: C_BLUE, strokeWidth: 0 }}
                                                    connectNulls
                                                />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </ChartCard>

                                </Box>
                            </Box>
                        )}
                    </>
                )}
                {activeTab === 0 && (
                    <Box sx={{ p: 2 }}>
                        <GHGScope1Section siteId={siteId} darkMode={darkMode} selectedDate={selectedDate} />
                    </Box>
                )}
            </Box>
        </>
    );
};

export default AnalysisSection;